import {
  ActivityExpressionVariablesRecord,
  StoreyExpressionVariables,
} from '@shared/models/design/design-expression-variables.model';
import { designGuards } from '../design-typeguards.helpers';
import { getPerimeterFromStoreys, getGfaFromStoreys } from '../shape.helpers';
import {
  getSharedLengthForPerimeter,
  getStoreyEnvelopeMetricsById,
} from '../envelope.helpers';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import type { Storey } from '@shared/models/design/storey.model';
import type { Activity } from '@shared/models/design/activities/activity.model';
import { getActivityMixFromStorey } from '../activity-mix.helpers';
import { getActivityExpressionVariablesRecord } from './activity-expression-variables.helpers';
import { getActivities } from '../activities.helpers';
import { getInnerHeightFromDesignHeight } from './storey-sum-from-design.helpers';
import {
  classifyCutoutKind,
  getCutoutShapes,
  getStoreyInnerPerimeter,
} from '../cutout.helpers';
import { computePolygonPerimeter } from '@shared/helpers/geometry/geometry.helpers';

/**
 * Expression variables for one building's storey stack.
 * Elevation stays inside this building so a sibling footprint is not treated
 * as floors above. Cutouts become atriums when a covering parent building
 * sits on the void.
 */
const getStoreyExpressionVariablesForStoreys = (
  design: BuildingDesign,
  storeys: Storey[],
  activities: Activity[],
  envelopeById: ReturnType<typeof getStoreyEnvelopeMetricsById>,
): StoreyExpressionVariables[] => {
  let elevation = storeys[0]?.elevation ?? 0;

  return storeys.map((storey, index) => {
    const basePerimeter = getPerimeterFromStoreys(storey);
    const gfa = getGfaFromStoreys(storey);
    const height = storey.height ?? 0;
    const isMezzanine = designGuards.is.mezzanineStorey(storey);
    const perimeterInner = getStoreyInnerPerimeter(storey);
    const courtyardInnerPerimeter = getCutoutShapes(storey).reduce(
      (sum, cutout) =>
        classifyCutoutKind(design, storey, cutout) === 'courtyard'
          ? sum + computePolygonPerimeter(cutout.points)
          : sum,
      0,
    );
    const envelope = envelopeById[storey.id];
    const sharedWallArea = envelope?.sharedWallArea ?? 0;
    // Spread party-wall area over storey height so perimeter * height = gwa.
    const sharedWallLength = getSharedLengthForPerimeter(envelope, height);
    const exposedOuterPerimeter = Math.max(0, basePerimeter - sharedWallLength);
    const perimeter = exposedOuterPerimeter + courtyardInnerPerimeter;
    // Taller neighbour keeps extra height as external wall (length × overlap only).
    const gwa = isMezzanine
      ? 0
      : Math.max(
          0,
          (basePerimeter + courtyardInnerPerimeter) * height - sharedWallArea,
        );

    const activityMix = getActivityMixFromStorey(design, storey);

    // Create a fully-populated record so downstream expression code never needs null checks
    const activityExpressionVariablesRecord: ActivityExpressionVariablesRecord =
      getActivityExpressionVariablesRecord(activities, activityMix);

    // Mostly for legacy compatibility, with the getStorey function.
    const name = storey.name ?? `Storey ${index}`;

    const variables: StoreyExpressionVariables = {
      name,
      gfa,
      height,
      inner_height: getInnerHeightFromDesignHeight(height),
      elevation: isMezzanine
        ? elevation - height // Mezzanine is hanging in the storey below
        : elevation,
      perimeter,
      perimeter_inner: perimeterInner,
      gwa,
      mezzanine: isMezzanine,
      is_ground_floor: Number(envelope?.isGroundFloor ?? index === 0),
      covered_gfa_below: envelope?.coveredGfaBelow ?? 0,
      covered_gfa_above: envelope?.coveredGfaAbove ?? 0,
      programs: activityExpressionVariablesRecord,
    };

    // Mezzanine storeys do not contribute to the elevation since theyre internal
    if (!isMezzanine) {
      elevation += height;
    }
    return variables;
  });
};

/**
 * Per-building storey expression stacks. Each building keeps its own elevation
 * so a second footprint is not treated as floors above the first.
 * @param design {@link BuildingDesign} to read storeys from
 */
export const getStoreysExpressionVariablesByBuilding = (
  design: BuildingDesign,
): StoreyExpressionVariables[][] => {
  const activities = getActivities(design);
  const envelopeById = getStoreyEnvelopeMetricsById(design);
  return design.buildings.map((building) =>
    getStoreyExpressionVariablesForStoreys(
      design,
      building.storeys,
      activities,
      envelopeById,
    ),
  );
};

/**
 * Expression variables for the first {@link Building}'s storeys. Site-wide
 * `getStorey(n)` sums floor `n` from every stack in
 * {@link getStoreysExpressionVariablesByBuilding}.
 * @param design {@link BuildingDesign} to read storeys from
 */
export const getStoreysExpressionVariables = (
  design: BuildingDesign,
): StoreyExpressionVariables[] =>
  getStoreysExpressionVariablesByBuilding(design)[0] ?? [];
